import { FC, useMemo } from 'react';

import { AccountingExportOption } from '__generated__/GQL';
import SunshineCard from 'components/_core/SunshineCard';

import locales from './locales';

interface ExportOption {
  description: string;
  id: AccountingExportOption;
  label: string;
  disabled?: boolean;
}

const EXPORT_OPTIONS: Record<AccountingExportOption, ExportOption> = {
  [AccountingExportOption.Transactions]: {
    description: locales.transactions.description,
    id: AccountingExportOption.Transactions,
    label: locales.transactions.title,
  },
  [AccountingExportOption.Receipts]: {
    description: locales.receipts.description,
    id: AccountingExportOption.Receipts,
    label: locales.receipts.title,
  },
  [AccountingExportOption.Invoices]: {
    description: locales.invoices.description,
    id: AccountingExportOption.Invoices,
    label: locales.invoices.title,
  },
  [AccountingExportOption.BankStatements]: {
    description: locales.bankStatements.description,
    id: AccountingExportOption.BankStatements,
    label: locales.bankStatements.title,
  },
};

interface ExportOptionsProps {
  disabledIds?: AccountingExportOption[];
  withBankStatement?: boolean;
  onChange: (options: AccountingExportOption[]) => void;
  selectedIds: AccountingExportOption[];
}

const ExportOptions: FC<ExportOptionsProps> = ({
  disabledIds = [],
  onChange,
  selectedIds,
  withBankStatement = true,
}) => {
  const items: ExportOption[] = useMemo(() => {
    const itemsArray = [
      EXPORT_OPTIONS.TRANSACTIONS,
      EXPORT_OPTIONS.RECEIPTS,
      EXPORT_OPTIONS.INVOICES,
    ];

    if (withBankStatement) {
      itemsArray.push(EXPORT_OPTIONS.BANK_STATEMENTS);
    }

    return itemsArray.map((item) => ({
      ...item,
      disabled: disabledIds.includes(item.id),
    }));
  }, [disabledIds, withBankStatement]);

  return (
    <SunshineCard.CheckboxGroup<AccountingExportOption>
      onChange={(options) => onChange(options)}
      value={selectedIds}
    >
      <SunshineCard.Group>
        {items.map((item) => (
          <SunshineCard
            isDisabled={disabledIds.includes(item.id)}
            key={item.id}
            title={item.label}
            value={item.id}
            variant="checkbox"
          >
            <SunshineCard.Content>
              <SunshineCard.Text>{item.description}</SunshineCard.Text>
            </SunshineCard.Content>
          </SunshineCard>
        ))}
      </SunshineCard.Group>
    </SunshineCard.CheckboxGroup>
  );
};

export default ExportOptions;
